
import React, { useState } from "react";
import Select from "react-select";
import tinycolor from "tinycolor2";
import { useTheme } from "../CommonComponents/ThemeProvider";
import PrimaryButton from "../CommonComponents/PrimaryButton";

export default function AppearanceSection() {
  const { themeId, setThemeId, themes, currentTheme } = useTheme();
  const [selectedTheme, setSelectedTheme] = useState(themeId);
  const [showConfirmation, setShowConfirmation] = useState(false);

  // Find the theme object for preview
  const previewTheme = themes.find(t => t.id === selectedTheme) || currentTheme;
  const themeOptions = themes.map((theme) => ({
    value: theme.id,
    label: theme.name,
  }));
  const selectedOption =
    themeOptions.find((option) => option.value === selectedTheme) || null;

  const handleThemeChange = (option) => {
    if (!option) return;
    setSelectedTheme(option.value);
    setShowConfirmation(true);
  };

  const handleConfirm = () => {
    setThemeId(selectedTheme);
    setShowConfirmation(false);
  };

  const handleCancel = () => {
    setSelectedTheme(themeId);
    setShowConfirmation(false);
  };

  const selectStyles = {
    control: (base, state) => ({
      ...base,
      minHeight: 52,
      borderRadius: 14,
      borderColor: state.isFocused ? previewTheme.color : "#d1d5db",
      boxShadow: state.isFocused
        ? `0 0 0 3px ${tinycolor(previewTheme.color).setAlpha(0.16).toRgbString()}`
        : "none",
      "&:hover": {
        borderColor: previewTheme.color,
      },
    }),
    valueContainer: (base) => ({
      ...base,
      padding: "0 14px",
    }),
    singleValue: (base) => ({
      ...base,
      color: "#111827",
      fontSize: 14,
      fontWeight: 500,
    }),
    placeholder: (base) => ({
      ...base,
      color: "#9ca3af",
      fontSize: 14,
    }),
    indicatorSeparator: () => ({ display: "none" }),
    dropdownIndicator: (base, state) => ({
      ...base,
      color: state.isFocused ? previewTheme.color : "#6b7280",
      "&:hover": {
        color: previewTheme.color,
      },
    }),
    menuPortal: (base) => ({
      ...base,
      zIndex: 10020,
    }),
    menu: (base) => ({
      ...base,
      zIndex: 10020,
      overflow: "hidden",
      borderRadius: 16,
      border: "1px solid #e5e7eb",
      boxShadow: "0 18px 40px rgba(15, 23, 42, 0.14)",
    }),
    option: (base, state) => ({
      ...base,
      fontSize: 14,
      backgroundColor: state.isSelected
        ? previewTheme.color
        : state.isFocused
          ? tinycolor(previewTheme.color).setAlpha(0.08).toRgbString()
          : "#ffffff",
      color: state.isSelected ? "#ffffff" : "#111827",
      "&:active": {
        backgroundColor: state.isSelected
          ? previewTheme.color
          : tinycolor(previewTheme.color).setAlpha(0.14).toRgbString(),
      },
    }),
  };

  return (
    <div className="flex flex-col gap-6 px-5 pb-5">
      <div>
        <label htmlFor="theme-select" className="block text-sm font-medium text-gray-700 mb-2">
          Select Theme
        </label>
        <Select
          inputId="theme-select"
          options={themeOptions}
          value={selectedOption}
          onChange={handleThemeChange}
          isSearchable={false}
          classNamePrefix="react-select"
          styles={selectStyles}
          menuPortalTarget={typeof document !== "undefined" ? document.body : null}
        />
      </div>

      {/* Color Preview */}
      <div className="flex justify-center">
        <div className="flex flex-col items-center">
          <div
            className="w-16 h-16 rounded-full border-4 border-gray-200 shadow-lg transition-colors"
            style={{ backgroundColor: previewTheme.color }}
          ></div>
          <span className="text-sm mt-3 font-medium" style={{ color: 'var(--color-text)', opacity: 0.7 }}>
            {previewTheme.name}
          </span>
        </div>
      </div>

      {/* Confirmation Buttons */}
      {showConfirmation && (
        <div className="flex gap-3 pt-4 border-t border-gray-100">
          <PrimaryButton
            onClick={handleConfirm}
            className="flex-1 px-4 py-2 rounded-lg text-sm font-medium text-white transition-colors"
            style={{ background: previewTheme.color }}
          >
            Apply Theme
          </PrimaryButton>
          <button
            onClick={handleCancel}
            className="flex-1 px-4 py-2 rounded-lg text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 transition-colors"
          >
            Cancel
          </button>
        </div>
      )}
    </div>
  );
}
